import { useMutation } from "@apollo/client";
import { useState, useEffect, useCallback } from "react";
import { useForm } from "react-hook-form";
import { useIntl } from "react-intl";
import { useNavigate } from "react-router-dom";

import { SignUpFormData, SignUpFormProps } from "../types";
import { validatePassword } from "../utils/validation";

import { SIGNUP_MUTATION } from "~/api/_mutations";
import { CURRENT_USER_QUERY } from "~/api/_queries";

interface UseSignUpReturn {
  email: string;
  loading: boolean;
  showPassword: boolean;
  showConfirmPassword: boolean;
  messages: any;
  errors: Record<string, any>;
  watch: (name: keyof SignUpFormData) => string;
  setValue: (
    name: keyof SignUpFormData,
    value: string,
    options?: { shouldValidate?: boolean },
  ) => void;
  trigger: (
    name?: keyof SignUpFormData | (keyof SignUpFormData)[],
  ) => Promise<boolean>;
  toggleShowPassword: () => void;
  toggleShowConfirmPassword: () => void;
  handleFormSubmit: (
    onSuccess?: () => void,
    onError?: (error: Error) => void,
  ) => (e?: React.BaseSyntheticEvent) => Promise<void>;
}

export const useSignUp = (props: SignUpFormProps = {}): UseSignUpReturn => {
  const {
    messageNamespace = "signup",
    email: emailProp,
    onSubmit: onSubmitProp,
    isLoading: isLoadingProp,
  } = props;

  const [email, setEmail] = useState(emailProp ?? "");
  const [internalLoading, setInternalLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [submitSignUp] = useMutation(SIGNUP_MUTATION);
  const navigate = useNavigate();
  const { messages } = useIntl() as { messages: any };

  const loading = isLoadingProp ?? internalLoading;
  const ns = messages?.login?.[messageNamespace];

  const getErrorMessages = () => {
    if (messageNamespace === "resetPassword") {
      return {
        passwordRequired: ns?.errors?.passwordRequired,
        passwordInvalid: ns?.errors?.minLength,
        confirmPasswordRequired: ns?.errors?.confirmRequired,
        passwordsMismatch: ns?.errors?.mismatch,
      };
    }
    return {
      passwordRequired: ns?.errors?.passwordRequired,
      passwordInvalid: ns?.errors?.passwordInvalid,
      confirmPasswordRequired: ns?.errors?.confirmPasswordRequired,
      passwordsMismatch: ns?.errors?.passwordsMismatch,
    };
  };

  const {
    handleSubmit,
    watch,
    setValue,
    trigger,
    formState: { errors },
  } = useForm<SignUpFormData>({
    mode: "onChange",
    resolver: (values) => {
      const errorMessages = getErrorMessages();
      const validationErrors: Record<
        string,
        { type: string; message: string }
      > = {};

      const passwordValidation = validatePassword(values.password, {
        passwordRequired: errorMessages.passwordRequired,
        passwordInvalid: errorMessages.passwordInvalid,
      });

      if (passwordValidation !== true) {
        validationErrors.password = {
          type: "validate",
          message: passwordValidation,
        };
      }

      if (!values.confirmPassword) {
        validationErrors.confirmPassword = {
          type: "required",
          message: errorMessages.confirmPasswordRequired,
        };
      } else if (values.confirmPassword !== values.password) {
        validationErrors.confirmPassword = {
          type: "validate",
          message: errorMessages.passwordsMismatch,
        };
      }

      return { values, errors: validationErrors };
    },
  });

  useEffect(() => {
    if (emailProp !== undefined) return;
    const urlParams = new URLSearchParams(window.location.search);
    const emailParam = urlParams.get("email");
    if (emailParam) {
      setEmail(emailParam);
    }
  }, []);

  useEffect(() => {
    if (emailProp !== undefined) {
      setEmail(emailProp);
    }
  }, [emailProp]);

  const toggleShowPassword = useCallback(() => {
    setShowPassword((prev) => !prev);
  }, []);

  const toggleShowConfirmPassword = useCallback(() => {
    setShowConfirmPassword((prev) => !prev);
  }, []);

  const onSubmit = useCallback(
    async (
      data: SignUpFormData,
      onError?: (error: Error) => void,
    ): Promise<void> => {
      if (onSubmitProp) {
        try {
          await onSubmitProp(data.password);
        } catch (e) {
          onError?.(e as Error);
        }
        return;
      }

      const urlParams = new URLSearchParams(window.location.search);
      const token = urlParams.get("token");

      setInternalLoading(true);
      try {
        await submitSignUp({
          variables: {
            input: { token, password: data.password },
          },
          awaitRefetchQueries: true,
          refetchQueries: [{ query: CURRENT_USER_QUERY }],
        });
        setInternalLoading(false);
        navigate({
          pathname: "/app",
          state: { welcome: true },
        });
      } catch (e) {
        setInternalLoading(false);
        onError?.(e as Error);
      }
    },
    [onSubmitProp, submitSignUp, history],
  );

  const handleFormSubmit = useCallback(
    (_onSuccess?: () => void, onError?: (error: Error) => void) => {
      return handleSubmit(
        (data) => onSubmit(data, onError),
        (formErrors) => {
          console.log("Form validation errors:", formErrors);
          setInternalLoading(false);
        },
      );
    },
    [handleSubmit, onSubmit],
  );

  return {
    email,
    loading,
    showPassword,
    showConfirmPassword,
    messages,
    errors,
    watch,
    setValue,
    trigger,
    toggleShowPassword,
    toggleShowConfirmPassword,
    handleFormSubmit,
  };
};
